import * as React from 'react';
import styled, {css} from 'styled-components';
import {FileObject, getFileByID} from 'api/library';
import ControlButton, {ControlButtonVariant} from 'components/ControlButton';
import validateResponse from 'helpers/validate-response';
import isError from 'helpers/isError';
import {IconLoading, IconTrash} from 'icons';

export enum ControlsPosition {
  Top = 'top',
  Center = 'center',
}

export interface ContainerProps {
  id: FileObject['id'];
  onDelete?(): void;
  children(file: FileObject): JSX.Element;
  controlsPosition?: ControlsPosition;
  controlsVariant?: ControlButtonVariant;
}

const Container = styled.div`
  position: relative;
`;

const Controls = styled.div<Pick<ContainerProps, 'controlsPosition'>>`
  display: inline-grid;
  align-items: center;
  position: absolute;
  right: 10px;
  z-index: 50;

  ${({controlsPosition}) => {
    switch (controlsPosition) {
      case ControlsPosition.Top: return css`
        top: 10px;
      `;
      case ControlsPosition.Center: return css`
        margin: auto;
        top: 0;
        bottom: 0;
      `;
    }
  }};  
`;

/**
 * This is a container for a file with a set of controls.
 * @param {ContainerProps} props
 * @return {React.ReactElement<ContainerProps>}
 * @constructor
 */
function FileContainer(
    props: ContainerProps,
): React.ReactElement<ContainerProps> {
  const [
    file,
    setFile,
  ] = React.useState<FileObject | undefined>(undefined);

  const [
    isLoaded,
    setLoaded,
  ] = React.useState<boolean>(false);

  React.useEffect(() => {
    setLoaded(false);

    setTimeout(() => {
      getFileByID(props.id).then((response) => {
        validateResponse(response);

        if (response && !isError(response)) {
          setFile(response);
        }

        setLoaded(true);
      });
    }, 600);
  }, [props.id]);

  /**
   * Render content.
   * @return {JSX.Element}
   */
  function renderContent(): JSX.Element | undefined {
    /**
     * Render controls.
     * @return {JSX.Element}
     */
    function renderControls(): JSX.Element {
      /**
       * Render delete control.
       * @return {JSX.Element}
       */
      function renderDeleteControl(): JSX.Element {
        /**
         * This click event handler.
         */
        function handleClick(): void {
          props.onDelete && props.onDelete();
        }

        return (
          <ControlButton
            type="button"
            onClick={handleClick}
            variant={props.controlsVariant}
          >
            <IconTrash />
          </ControlButton>
        );
      }

      return (
        <Controls controlsPosition={props.controlsPosition}>
          {renderDeleteControl()}
        </Controls>
      );
    }

    return file ?(
      <Container>
        {renderControls()}
        {props.children(file)}
      </Container>
    ) : undefined;
  }

  /**
   * Render not found.
   * @return {JSX.Element}
   */
  function renderNotFound(): JSX.Element {
    return (
      <span>Изображение удалено с сервера</span>
    );
  }

  return (
    <React.Fragment>
      {isLoaded ? (
        file ? renderContent() : renderNotFound()
      ) : <IconLoading />}
    </React.Fragment>
  );
}

FileContainer.defaultProps = {
  controlsPosition: ControlsPosition.Top,
  controlsVariant: ControlButtonVariant.Accent,
};

export default FileContainer;
